import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { getData } from '../../../webService/webservice.js';

const LIMIT_OPTIONS = [5, 10, 25, "all"];

const StockImagePicker = ({ onSelect, onCancel }) => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [limit, setLimit] = useState(5);

    useEffect(() => {
        fetchImages(currentPage);
    }, [currentPage, limit]);

    const fetchImages = async (page) => {
        try {
            setLoading(true);
            const res = await getData("stockimages/stock-images", { 
                page, 
                limit: limit === "all" ? 1000 : limit 
            });
            if (res?.status === 200) {
                const data = res.data;
                setImages(data?.results || []);
                setCurrentPage(data?.currentPage || page);
                setTotalPages(data?.totalPages || 1);
                setTotalItems(data?.totalItems || 0);
            }
        } catch (err) {
            console.error("Fetch stock images error:", err);
        } finally {
            setLoading(false);
        }
    };

    const getImageUrl = (img) => {
        const uri = img.uri || img.fileObject?.uri;
        if (!uri) return "";
        const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
        return `${baseUrl}/${uri.replace(/^\/+/, "")}`;
    };

    const handlePageChange = (direction) => {
        setCurrentPage((prev) => {
            if (direction === "first") return 1;
            if (direction === "last") return totalPages;
            if (direction === "next") return Math.min(prev + 1, totalPages);
            if (direction === "prev") return Math.max(prev - 1, 1);
            return prev;
        });
    };

    const handleItemsPerPageChange = (e) => {
        const value = e.target.value;
        setLimit(value === "all" ? "all" : Number(value));
        setCurrentPage(1);
    };

    const startItem = totalItems === 0 ? 0 : (currentPage - 1) * (limit === "all" ? totalItems : limit) + 1;
    const endItem = limit === "all" ? totalItems : Math.min(currentPage * limit, totalItems);

    return (
        <div className="sip-overlay">
            <div className="sip-card">
                {/* Header */}
                <div className="sip-header">
                    <div className="sip-header-left">
                        <Icon icon="mdi:image-multiple" className="sip-header-icon" />
                        <div>
                            <h3 className="sip-title">Stock Images</h3>
                            <p className="sip-subtitle">Pick a professional image from the library</p>
                        </div>
                    </div>
                    <button className="sip-close-btn" onClick={onCancel} title="Close">
                        <Icon icon="mdi:close" />
                    </button>
                </div>

                {/* Grid */}
                <div className="sip-body">
                    {loading ? (
                        <div className="sip-loader">
                            <Icon icon="mdi:loading" className="sip-spin" />
                            <p>Loading images...</p>
                        </div>
                    ) : images.length > 0 ? (
                        <div className="sip-grid">
                            {images.map((img, index) => {
                                const imageUrl = getImageUrl(img);
                                const name = img.originalname || img.filename || "Stock Image";
                                return (
                                    <div
                                        key={img._id || index}
                                        className="sip-item"
                                        onClick={() => onSelect(img.fileObject || img)}
                                        title={name}
                                    >
                                        <img src={imageUrl} alt={name} />
                                        <div className="sip-item-overlay">
                                            <Icon icon="mdi:check-circle" className="sip-check-icon" />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="sip-empty">
                            <Icon icon="mdi:image-off-outline" className="sip-empty-icon" />
                            <p>No stock images available</p>
                        </div>
                    )}
                </div>

                {/* Pagination (Standardized) */}
                {!loading && totalItems > 0 && (
                    <div className="sip-pagination">
                        <div className="sip-pagination-entries">
                            <span className="sip-text-label">Show</span>
                            <select 
                                className="sip-entries-select"
                                value={limit}
                                onChange={handleItemsPerPageChange}
                            >
                                {LIMIT_OPTIONS.map(opt => (
                                    <option key={opt} value={opt}>{opt === "all" ? "All" : opt}</option>
                                ))}
                            </select>
                            <span className="sip-text-label">Entries</span>
                        </div>

                        <div className="sip-showing-range">
                            {limit === "all" 
                                ? `Showing all ${totalItems} entries` 
                                : `Showing ${startItem} to ${endItem} of ${totalItems} entries`}
                        </div>

                        <div className="sip-page-btns">
                            <button
                                className="sip-page-btn"
                                onClick={() => handlePageChange("first")}
                                disabled={currentPage === 1}
                                title="First"
                            >
                                <Icon icon="mdi:chevron-double-left" />
                            </button>
                            <button
                                className="sip-page-btn"
                                onClick={() => handlePageChange("prev")}
                                disabled={currentPage === 1}
                                title="Previous"
                            >
                                <Icon icon="mdi:chevron-left" />
                            </button>
                            <button
                                className="sip-page-btn"
                                onClick={() => handlePageChange("next")}
                                disabled={currentPage === totalPages}
                                title="Next"
                            >
                                <Icon icon="mdi:chevron-right" />
                            </button>
                            <button
                                className="sip-page-btn"
                                onClick={() => handlePageChange("last")}
                                disabled={currentPage === totalPages}
                                title="Last"
                            >
                                <Icon icon="mdi:chevron-double-right" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <style>{`
                .sip-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0, 0, 0, 0.55);
                    backdrop-filter: blur(4px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 9999;
                    padding: 16px;
                }
                .sip-card {
                    background: #ffffff;
                    border-radius: 20px;
                    width: 100%;
                    max-width: 860px;
                    max-height: 88vh;
                    display: flex;
                    flex-direction: column;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
                    overflow: hidden;
                }

                .sip-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 20px 24px;
                    border-bottom: 1px solid #f1f5f9;
                    flex-shrink: 0;
                }
                .sip-header-left {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                .sip-header-icon {
                    font-size: 28px;
                    color: #3C0008;
                }
                .sip-title {
                    margin: 0;
                    font-size: 18px;
                    font-weight: 700;
                    color: #1e293b;
                }
                .sip-subtitle {
                    margin: 2px 0 0;
                    font-size: 12px;
                    color: #64748b;
                    font-weight: 500;
                }
                .sip-close-btn {
                    background: #f1f5f9;
                    border: none;
                    border-radius: 10px;
                    width: 36px;
                    height: 36px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 20px;
                    cursor: pointer;
                    color: #64748b;
                    transition: all 0.2s;
                }
                .sip-close-btn:hover {
                    background: #e2e8f0;
                    color: #3C0008;
                }

                .sip-body {
                    flex: 1;
                    overflow-y: auto;
                    padding: 20px 24px;
                }
                .sip-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
                    gap: 14px;
                }
                .sip-item {
                    position: relative;
                    border-radius: 12px;
                    overflow: hidden;
                    cursor: pointer;
                    aspect-ratio: 1;
                    border: 2px solid transparent;
                    transition: all 0.2s ease;
                    background: #f8fafc;
                }
                .sip-item:hover {
                    border-color: #3C0008;
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(60, 0, 8, 0.12);
                }
                .sip-item img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    display: block;
                }
                .sip-item-overlay {
                    position: absolute;
                    inset: 0;
                    background: rgba(60, 0, 8, 0.45);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    opacity: 0;
                    transition: opacity 0.2s;
                }
                .sip-item:hover .sip-item-overlay {
                    opacity: 1;
                }
                .sip-check-icon {
                    font-size: 32px;
                    color: #ffffff;
                }

                .sip-loader {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 12px;
                    padding: 60px 20px;
                    color: #94a3b8;
                }
                .sip-spin {
                    font-size: 32px;
                    animation: sipSpin 0.9s linear infinite;
                }
                @keyframes sipSpin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                .sip-empty {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 12px;
                    padding: 60px 20px;
                    color: #94a3b8;
                }
                .sip-empty-icon {
                    font-size: 48px;
                    color: #cbd5e1;
                }

                .sip-pagination {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 16px 24px;
                    border-top: 1px solid #f1f5f9;
                    flex-shrink: 0;
                    gap: 12px;
                    flex-wrap: wrap;
                }
                .sip-pagination-entries {
                    display: flex;
                    align-items: center;
                }
                .sip-text-label {
                    color: rgba(0, 0, 0, 0.55);
                    font-size: 13px;
                    font-weight: 600;
                    margin: 0 4px;
                }
                .sip-entries-select {
                    padding: 4px 6px;
                    font-size: 13px;
                    border-radius: 8px;
                    border: 1px solid #ccc;
                    outline: none;
                }
                .sip-showing-range {
                    font-size: 13px;
                    font-weight: 600;
                    color: #525252;
                }
                .sip-page-btns {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .sip-page-btn {
                    min-width: 38px;
                    height: 38px;
                    border: none;
                    border-radius: 18px;
                    background: linear-gradient(#3C0008, #a52a2a);
                    color: #ffffff;
                    font-size: 18px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s ease;
                    box-shadow: 0 2px 8px rgba(60, 0, 8, 0.2);
                }
                .sip-page-btn:hover:not(:disabled) {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(60, 0, 8, 0.3);
                }
                .sip-page-btn:disabled {
                    background: #e2e8f0;
                    color: #3C0008;
                    cursor: not-allowed;
                    box-shadow: none;
                }
            `}</style>
        </div>
    );
};

export default StockImagePicker;
